import HighchartsReact from 'highcharts-react-official';
import { CampaignAnalyticsPieChart } from 'modules/campaignAnalytics/components/CampaignAnalyticsPieChart';
import {
  MetricBreakdownContent,
  WideCardContainer,
  WideCardLeft,
  WideCardRight,
} from 'modules/campaignAnalytics/components/Styled';
import { PieChartData, ViewState } from 'modules/campaignAnalytics/types';
import { Locale } from 'modules/domains';
import { useMemo, useRef } from 'react';
import { FlexSpacer } from 'ui/flexSpacer';
import { EmptyCard } from '../EmptyCard';
import { LoadingCard } from '../LoadingCard';
import { ListContent } from './ListContent';
import { useCampaignQuickReplies } from './hooks';
import { QuickReplyStats } from './types';

export type CardContentProps = {
  locale?: Locale;
};

export const CardContent = ({ locale }: CardContentProps) => {
  const {
    data: { campaignQuickReplies } = {},
    isLoading,
  } = useCampaignQuickReplies(locale?.tag);
  const chartRef = useRef<HighchartsReact | null>(null);

  const { quickReplyStatsList, graphData } = useMemo(() => {
    if (!campaignQuickReplies?.totalQuickReplies) {
      return { quickReplyStatsList: [], graphData: [] };
    }

    const { totalQuickReplies, quickReplyStats } = campaignQuickReplies;

    const quickReplyStatsList: QuickReplyStats[] = [];
    const graphData: PieChartData = [];

    quickReplyStats.forEach(({ name, count }) => {
      const percentage = count / totalQuickReplies;

      quickReplyStatsList.push({
        name,
        value: count,
        percentage,
      });

      graphData.push({
        name,
        y: percentage,
      });
    });

    return { quickReplyStatsList, graphData };
  }, [campaignQuickReplies]);

  let viewState: ViewState;

  if (isLoading) {
    viewState = ViewState.Loading;
  } else if (!campaignQuickReplies || campaignQuickReplies.totalQuickReplies === 0) {
    viewState = ViewState.NoData;
  } else {
    viewState = ViewState.ContentReady;
  }

  switch (viewState) {
    case ViewState.Loading:
      return <LoadingCard height={450} />;
    case ViewState.NoData:
      return (
        <FlexSpacer width="100%" justifyContent="center">
          <EmptyCard type="quick" />
        </FlexSpacer>
      );
    case ViewState.ContentReady:
      return (
        <MetricBreakdownContent noPadding>
          <WideCardContainer>
            <WideCardLeft showBorder>
              <ListContent
                locale={locale}
                totalReplies={campaignQuickReplies!.totalQuickReplies}
                quickReplyStats={quickReplyStatsList}
              />
            </WideCardLeft>
            <WideCardRight data-test="quick-replies-pie-chart">
              <CampaignAnalyticsPieChart data={graphData} ref={chartRef} />
            </WideCardRight>
          </WideCardContainer>
        </MetricBreakdownContent>
      );
    default:
      return null;
  }
};
